import { useRef, useState } from 'react';
import { FileArchive, FileImage, FileSpreadsheet, FileText, FileType, Loader2, Paperclip, Upload, type LucideIcon } from 'lucide-react';
import { SituacaoArquivo, TipoAlvo, formatarDiaMes, formatarTamanho, type DocumentoLicitacaoDto } from '@nexital/shared';
import { tipoDoArquivo, type TipoArquivo } from '../../domain/licitacao';
import { PontaItem, atributosAlvo } from './PontaItem';
import { useLicitacao } from './contexto';
import { Modulo } from './Modulo';

/** Lista vazia fixa: o seletor do store não pode devolver uma lista nova a cada leitura (laço infinito no React). */
const NENHUM: never[] = [];

const ICONE: Record<TipoArquivo, LucideIcon> = {
  pdf: FileText,
  word: FileType,
  planilha: FileSpreadsheet,
  imagem: FileImage,
  zip: FileArchive,
  outro: Paperclip,
};

const SITUACAO: Record<SituacaoArquivo, { texto: string; classe: string }> = {
  [SituacaoArquivo.Pendente]: { texto: 'Pendente', classe: 'pilula--aguardando' },
  [SituacaoArquivo.Enviando]: { texto: 'Enviando', classe: 'pilula--informacao' },
  [SituacaoArquivo.Disponivel]: { texto: 'Disponível', classe: 'pilula--sucesso' },
  [SituacaoArquivo.Falhou]: { texto: 'Falhou no envio', classe: 'pilula--urgencia' },
};

const FORMATOS = '.pdf,.png,.jpg,.jpeg,.tif,.tiff,.bmp,.zip,.doc,.docx,.rtf,.odt,.xls,.xlsx,.ods,.csv,.txt,.md,.html,.htm,.json,.xml,.log';

function CartaoDocumento({ documento }: { documento: DocumentoLicitacaoDto }) {
  const obterUrl = useLicitacao((s) => s.urlDocumento);
  const tipo = tipoDoArquivo(documento.extensao);
  const Icone = ICONE[tipo];
  const situacao = SITUACAO[documento.situacao];
  // A janela abre no clique (senão o navegador bloqueia) e recebe a URL assinada quando ela chega (RQ-ARQ-006).
  const abrir = async () => {
    const janela = window.open('', '_blank');
    const url = await obterUrl(documento.uuid);
    if (url && janela) janela.location.href = url.url;
    else janela?.close();
  };
  return (
    <li className="alvo-item" {...atributosAlvo(TipoAlvo.Documento, documento.uuid)}>
      <PontaItem uuid={documento.uuid} />
      <button type="button" className="documento" onClick={() => void abrir()} disabled={documento.situacao !== SituacaoArquivo.Disponivel}>
        <span className={`documento__icone documento__icone--${tipo}`} aria-hidden>
          <Icone size={22} />
        </span>
        <span className="documento__dados">
          <strong className="documento__nome">{documento.nome}</strong>
          <span className={`pilula ${situacao.classe}`}>{situacao.texto}</span>
          <span className="documento__detalhe">
            {formatarTamanho(documento.tamanhoBytes)} • {formatarDiaMes(documento.enviadoEm)}
            {documento.enviadoPor && ` • ${documento.enviadoPor.nome.split(' ')[0]}`}
          </span>
        </span>
      </button>
    </li>
  );
}

/** "Documentos" (RQ-LIC-002): envio pelo servidor (RQ-ARQ-002), arrastando arquivos ou pelo botão. */
export function ModuloDocumentos() {
  const documentos = useLicitacao((s) => s.dados?.documentos ?? NENHUM);
  const limite = useLicitacao((s) => s.dados?.limiteArquivoMb ?? 50);
  const enviando = useLicitacao((s) => s.enviando);
  const enviar = useLicitacao((s) => s.enviarDocumentos);
  const seletor = useRef<HTMLInputElement>(null);
  const [sobre, definirSobre] = useState(false);

  const aoSoltar = (evento: React.DragEvent) => {
    evento.preventDefault();
    definirSobre(false);
    void enviar([...evento.dataTransfer.files]);
  };
  const extra = <span className="modulo__contador">{documentos.length === 1 ? '1 documento' : `${documentos.length} documentos`}</span>;
  const acoes = (
    <button type="button" className="botao-icone" onClick={() => seletor.current?.click()} aria-label="Enviar documento">
      <Upload size={18} />
    </button>
  );
  return (
    <Modulo titulo="Documentos" Icone={Paperclip} cor="amarelo" extra={extra} acoes={acoes}>
      <div
        className={`documentos${sobre ? ' documentos--recebendo' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          definirSobre(true);
        }}
        onDragLeave={() => definirSobre(false)}
        onDrop={aoSoltar}
      >
        <ul className="documentos__lista">
          {documentos.map((d) => <CartaoDocumento key={d.uuid} documento={d} />)}
          {enviando.map((nome) => (
            <li key={nome} className="documento documento--enviando" aria-live="polite">
              <Loader2 size={18} className="girando" aria-hidden /> Enviando {nome}…
            </li>
          ))}
        </ul>
        <button type="button" className="documentos__soltar" onClick={() => seletor.current?.click()}>
          <Upload size={16} aria-hidden /> Arraste arquivos aqui ou clique para enviar (até {limite} MB)
        </button>
        <input
          ref={seletor}
          type="file"
          multiple
          hidden
          accept={FORMATOS}
          onChange={(e) => {
            void enviar([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
        />
      </div>
    </Modulo>
  );
}
